/**
 * ThemeContext — provider global de tema (A3.4 / Master Plan A3).
 *
 * Responsabilidades:
 *   1. Carregar preferência salva via `GET /api/v1/dashboard/tema` no mount.
 *   2. Calcular tema efetivo (`light` | `dark`) via `resolveEffectiveTheme`.
 *   3. Aplicar/remover classe `dark` no `<html>` document element.
 *   4. Persistir mudanças com debounce 800ms (evita flood do backend).
 *   5. Escutar `prefers-color-scheme` do SO + relógio (1min) pro modo `sistema`/`automatico`.
 *   6. Expor `useTheme()` com `{ mode, effective, isLoading, setMode, setAutoRange }`.
 *
 * Race-safe: lida com unmount via mounted ref.
 * Fail-graceful: erro no fetch tema cai pro default `automatico` 19:00–07:00.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

import { fetchTema, saveTema } from '@/lib/dashboard/layouts-api';
import type { ThemeMode } from '@/lib/dashboard/layouts-api';
import { resolveEffectiveTheme, type EffectiveTheme } from '@/lib/theme/resolve-theme';
import { useAuth } from '@/contexts/AuthContext';

interface ThemeContextValue {
  mode: ThemeMode;
  effective: EffectiveTheme;
  autoInicioHhmm: string | null;
  autoFimHhmm: string | null;
  isLoading: boolean;
  setMode: (mode: ThemeMode) => void;
  setAutoRange: (inicio: string | null, fim: string | null) => void;
}

const DEFAULT_MODE: ThemeMode = 'sistema';
const SAVE_DEBOUNCE_MS = 800;
const CLOCK_TICK_MS = 60_000;

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { user } = useAuth();
  const [mode, setModeState] = useState<ThemeMode>(DEFAULT_MODE);
  const [autoInicioHhmm, setAutoInicioHhmm] = useState<string | null>(null);
  const [autoFimHhmm, setAutoFimHhmm] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false;
  });
  // Tick a cada 1min pra automatico recomputar efetivo no horário da janela.
  const [, setNowTick] = useState(0);

  const mounted = useRef(true);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadDone = useRef(false);

  // Cleanup mount
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  // 1) Carrega preferência salva quando user logar
  useEffect(() => {
    if (!user || user.tipo === 'tecnico') {
      // Sem user: usa defaults. Não consulta backend. O técnico de campo (25/10/2026) também não: o servidor recusa a
      // preferência de tema para ele (só o aplicativo do técnico), e a recusa acendia o aviso "sem acesso".
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    fetchTema()
      .then((tema) => {
        if (cancelled || !mounted.current) return;
        setModeState(tema.theme_mode ?? DEFAULT_MODE);
        setAutoInicioHhmm(tema.theme_auto_inicio_hhmm);
        setAutoFimHhmm(tema.theme_auto_fim_hhmm);
        initialLoadDone.current = true;
      })
      .catch(() => {
        // Erro no fetch — mantém defaults. Loga só em dev.
        if (import.meta.env.DEV) {
          console.warn('[theme] fetchTema falhou, usando defaults');
        }
      })
      .finally(() => {
        if (!cancelled && mounted.current) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // 2) Listener prefers-color-scheme
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      if (mounted.current) setSystemPrefersDark(e.matches);
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // 3) Tick relógio (1min) pra modo automatico
  useEffect(() => {
    if (mode !== 'automatico') return;
    const interval = setInterval(() => {
      if (mounted.current) setNowTick((t) => (t + 1) % 1_000_000);
    }, CLOCK_TICK_MS);
    return () => clearInterval(interval);
  }, [mode]);

  // 4) Computa efetivo + aplica classe `dark`
  const effective: EffectiveTheme = useMemo(
    () =>
      resolveEffectiveTheme({
        mode,
        autoInicioHhmm,
        autoFimHhmm,
        systemPrefersDark,
      }),
    [mode, autoInicioHhmm, autoFimHhmm, systemPrefersDark]
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (effective === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [effective]);

  // 5) Persiste no backend com debounce — só após load inicial
  const persistTema = useCallback(
    (next: { mode: ThemeMode; inicio: string | null; fim: string | null }) => {
      if (!user || !initialLoadDone.current) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveTema({
          themeMode: next.mode,
          inicioHhmm: next.inicio,
          fimHhmm: next.fim,
        }).catch(() => {
          if (import.meta.env.DEV) {
            console.warn('[theme] saveTema falhou — preferencia nao persistida');
          }
        });
      }, SAVE_DEBOUNCE_MS);
    },
    [user]
  );

  const setMode = useCallback(
    (next: ThemeMode) => {
      setModeState(next);
      persistTema({ mode: next, inicio: autoInicioHhmm, fim: autoFimHhmm });
    },
    [persistTema, autoInicioHhmm, autoFimHhmm]
  );

  const setAutoRange = useCallback(
    (inicio: string | null, fim: string | null) => {
      setAutoInicioHhmm(inicio);
      setAutoFimHhmm(fim);
      persistTema({ mode, inicio, fim });
    },
    [persistTema, mode]
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      effective,
      autoInicioHhmm,
      autoFimHhmm,
      isLoading,
      setMode,
      setAutoRange,
    }),
    [mode, effective, autoInicioHhmm, autoFimHhmm, isLoading, setMode, setAutoRange]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Fallback inerte usado quando `useTheme` é chamado fora do provider.
 * Cenário comum: testes unitários que renderizam components que dependem
 * indiretamente do TopBar/ThemeToggle sem encapsular o ThemeProvider.
 * Em produção o ThemeProvider está sempre montado no main.tsx.
 */
const NOOP_THEME_CONTEXT: ThemeContextValue = {
  mode: DEFAULT_MODE,
  effective: 'light',
  autoInicioHhmm: null,
  autoFimHhmm: null,
  isLoading: false,
  setMode: () => {},
  setAutoRange: () => {},
};

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  return ctx ?? NOOP_THEME_CONTEXT;
}
